import { useEffect, useState } from "react";
import {
  TrendingDown,
  AlertTriangle,
  CreditCard,
  Calendar,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import ApplyLoan from "./ApplyLoanPage";

// ✅ Safe number formatter
const nf = (v: any) => {
  const n = Number(v);
  if (isNaN(n)) return "0";
  return n.toLocaleString();
};

// ✅ Safe date formatter
const formatDate = (date: any) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

export default function HomePage() {
  const [loan, setLoan] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const loanRes = await apiFetch<any>("/home");
        const txRes = await apiFetch<any>("/recent-payment");

        if (!alive) return;

        setLoan(loanRes.loan);
        setTransactions(txRes.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const progress =
    loan?.total_due > 0
      ? Math.min((loan.paid / loan.total_due) * 100, 100)
      : 0;
  const [userName, setUserName] = useState<string>("Account");
  useEffect(() => {
    const stored =
      localStorage.getItem("userData") || sessionStorage.getItem("userData");

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.name) setUserName(parsed.name);
      } catch { }
    }
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">
            {greeting},  {userName} 👋
          </h1>
          <p className="text-muted-foreground text-sm">
            Financial overview of your active loan
          </p>
        </div>
      </div>

      {/* 🔥 STAT CARDS */}
      {[1, 6].includes(Number(loan?.loan_status)) ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Loan Amount */}
            <div className="relative rounded-xl p-5 bg-gradient-to-br from-green-600 to-green-500 text-white shadow-xl">
              <div className="flex justify-between">
                <p className="text-xs uppercase opacity-80">Loan Amount</p>
                <CreditCard className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold mt-2">
                {loading ? "—" : `KSh ${nf(loan?.loan_amount)}`}
              </p>
              <p className="text-xs opacity-80 mt-1">
                Status: {loan?.STATUS || "-"}
              </p>
            </div>

            {/* Balance */}
            <div className="relative rounded-xl p-5 bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-xl">
              <div className="flex justify-between">
                <p className="text-xs uppercase opacity-80">Balance</p>
                <TrendingDown className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold mt-2">
                {loading ? "—" : `KSh ${nf(loan?.balance)}`}
              </p>
              <p className="text-xs opacity-80 mt-1">Outstanding</p>
            </div>

            {/* Due Date */}
            <div className="relative rounded-xl p-5 bg-gradient-to-br from-orange-500 to-orange-400 text-white shadow-xl">
              <div className="flex justify-between">
                <p className="text-xs uppercase opacity-80">Due Date</p>
                <Calendar className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-xl font-bold mt-2">
                {loading ? "—" : formatDate(loan?.due_date)}
              </p>
              <p className="text-xs opacity-80 mt-1">
                Days: {loan?.loan_days || "-"}
              </p>
            </div>

            {/* Penalty */}
            <div className="relative rounded-xl p-5 bg-gradient-to-br from-purple-600 to-purple-500 text-white shadow-xl">
              <div className="flex justify-between">
                <p className="text-xs uppercase opacity-80">Penalty</p>
                <AlertTriangle className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold mt-2">
                {loading ? "—" : `KSh ${nf(loan?.penalty_charged)}`}
              </p>
              <p className="text-xs opacity-80 mt-1">
                {loan?.DEFAULTED_DAYS || 0} days overdue
              </p>
            </div>
          </div>

          {/* 📊 SUMMARY + ACTIONS */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* SUMMARY */}
            <div className="lg:col-span-2 bg-card rounded-xl p-6 shadow-md">
              <h2 className="font-semibold mb-4 text-lg">
                Loan Summary
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Paid</p>
                  <p className="font-semibold text-green-600">
                    KSh {nf(loan?.paid)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Total Due</p>
                  <p className="font-semibold">
                    KSh {nf(loan?.total_due)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Balance</p>
                  <p className="font-semibold text-red-500">
                    KSh {nf(loan?.balance)}
                  </p>
                </div>
              </div>

              {/* PROGRESS */}
              <div className="mt-5">
                <div className="flex justify-between text-xs mb-1">
                  <span>Repayment Progress</span>
                  <span>{progress.toFixed(0)}%</span>
                </div>

                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="bg-card rounded-xl p-6 shadow-md">
              <h2 className="font-semibold mb-4 text-lg">
                Quick Actions
              </h2>

              <div className="space-y-3">
                <Link
                  to="/dashboard/payment"
                  className="flex justify-between items-center p-3 rounded-lg bg-muted hover:bg-accent transition"
                >
                  <span>Pay Now</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/dashboard/statement"
                  className="flex justify-between items-center p-3 rounded-lg bg-muted hover:bg-accent transition"
                >
                  <span>View Statement</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* 💳 TRANSACTIONS */}
          <div className="bg-card rounded-xl p-6 shadow-md">
            <div className="flex justify-between mb-4">
              <h2 className="font-semibold text-lg">
                Recent Payments
              </h2>

              <Link
                to="/dashboard/statement"
                className="text-primary text-sm hover:underline"
              >
                View all
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Reference</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={3} className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-4">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx) => (
                      <tr
                        key={tx.id}
                        className="border-b hover:bg-muted/40 transition"
                      >
                        <td className="py-3">
                          {formatDate(tx.created_at)}
                        </td>

                        <td className="py-3 font-mono text-xs">
                          {tx.transaction_code}
                        </td>

                        <td className="py-3 text-right font-semibold text-green-600">
                          KSh {nf(tx.transaction_amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* ELSE → any other status */}
        
          <ApplyLoan />
        </>
      )}
    </div>
  );
}